import { Header } from "shared/components/BasicHeader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import css from "./styles.module.scss";

import school from "assets/images/school-1.png";
import birthday from "assets/images/birthday.png";
import { Text } from "shared/components/Text";

import { OfferItem } from "./OfferItem";
import gradientBar from "assets/images/gradient-bar.png";
import cubes from "assets/images/cubes.png";
import { splitArrayToChunks } from "shared/utils";
import { Button } from "shared/components/Button";
import i18n from "i18n";
import { Footer } from "shared/components/Footer";
import cn from "classnames";
import { useRef, useState, useEffect, useMemo } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { LoadingOverlay } from "shared/components/LoadingOverlay";
import { getCity, getLocations } from "store/selectors";
import { appStore } from "store";
import { BackgroundCubes } from "shared/components/BackgroundCubes";
import { AdditionalsView } from "./AdditionalsView";
import { IntegrationForm } from "../IntegrationForm";
import { FAQList } from "shared/components/FAQList";
import { VideoPlayer } from "shared/components/VideoPlayer";

const IMAGES_PER_ROW = 3;

export const IntegrationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const topRef = useRef();
  const cubesContainerRef = useRef();
  const { locations } = appStore.useState(getLocations);
  const { city } = appStore.useState(getCity);
  const siteTitle = locations.cities[city].cityOptions.siteTitle;
  const nameKey = locations.cities[city].cityOptions.nameKey;
  const isMobile = window.matchMedia("(max-width: 1060px)").matches;
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const footerScrollCallback = () => {
    topRef.current.scrollIntoView({ behavior: "smooth" });
    topRef.current.focus();
  };
  const integrationPageContent =
    locations.cities[city].cooperationSection.integrationPage;

  useEffect(() => {
    if (!locations.cities[city].cooperationSection.isHrefEnabled) {
      navigate(`/${city}`);
      return;
    }
    document.title = `${siteTitle} - Integrations`;
  }, []);

  const currentCityConfig = useMemo(() => {
    return locations.cities[city];
  }, [city, locations]);
  const [content, setContent] = useState([]);
  useEffect(() => {
    const contentFromTranslations = t(`integrationPage.content.${nameKey}`, {
      returnObjects: true,
    });
    contentFromTranslations.map((el) => {
      el.isAnswerVisible = false;
    });
    setContent(contentFromTranslations);
  }, [i18n.language, city]);

  const videoID = !isMobile
    ? integrationPageContent.backgroundVideoID
    : integrationPageContent.mobileBackgroundVideoID;

  const splittedIntegrationImages = useMemo(() => {
    return splitArrayToChunks({
      array: integrationPageContent.images,
      chunkSize: IMAGES_PER_ROW,
    });
  }, []);

  return (
    <div className={css.IntegrationPage}>
      <div className={css.recaptchaContainer} id="recaptchaContainer" />
      {isSubmitLoading && <LoadingOverlay />}
      <Header
        city={city}
        cityName={locations.cities[city]?.cityOptions.title || ""}
      />
      <div className={css.integrationVideoContainer}>
        <VideoPlayer
          videoID={videoID}
          autoplay={true}
          className={css.integrationVideoContainerBlur}
        />
        <div className={css.videoContentContainer}>
          <Button
            text={t("events.bookingButton")}
            className={css.bookingButton}
            onClick={() => {
              navigate(`/${city}/booking`);
            }}
          />
          <Text
            className={css.videoContainerText}
            type="white"
            bold
            secondaryFamily
            size="subTitle"
          >
            {t("integrationPage.titles.videoText")}
          </Text>
        </div>
      </div>
      <div className={css.relativeContainer} ref={cubesContainerRef}>
        <BackgroundCubes
          className={css.cubes}
          parentElement={cubesContainerRef}
        />
        <div className={css.flexContainer}>
          <div className={css.textDescriptionContainer}>
            <Text
              bold
              alignCenter
              type="white"
              size="sectionTitle"
              className={css.sectionTitle}
              secondaryFamily
            >
              {t("aboutSection.title")}
            </Text>
            <Text alignCenter type="white" size="body" className={css.textBody}>
              {t("integrationPage.body.aboutUs")}
            </Text>
          </div>
          <div className={css.textDescriptionContainer}>
            <Text
              bold
              alignCenter
              type="white"
              size="sectionTitle"
              className={css.sectionTitle}
              secondaryFamily
            >
              {t("integrationPage.titles.package")}
            </Text>
            <table className={css.packageTable}>
              <thead className={css.tableHead}>
                <tr className={css.columnTitles}>
                  <th>
                    <Text
                      className={css.flexItem}
                      bold
                      secondaryFamily
                      alignCenter
                      type="white"
                      size="body"
                    >
                      {t("integrationPage.packages.headerPackage")}
                    </Text>
                  </th>
                  <th>
                    <Text
                      className={css.flexItem}
                      bold
                      secondaryFamily
                      alignCenter
                      type="white"
                      size="body"
                    >
                      {t("integrationPage.packages.headerPrice")}
                    </Text>
                  </th>
                  <th>
                    <Text
                      className={css.flexItem}
                      bold
                      secondaryFamily
                      alignCenter
                      type="white"
                      size="body"
                    >
                      {t("integrationPage.packages.headerBoards")}
                    </Text>
                  </th>
                  <th>
                    <Text
                      className={css.flexItem}
                      bold
                      secondaryFamily
                      alignCenter
                      type="white"
                      size="body"
                    >
                      {t("integrationPage.packages.headerPlayers")}
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {integrationPageContent.packages.map((packageItem, index) => (
                  <tr
                    key={packageItem.name}
                    className={cn({
                      [css.evenRow]: index % 2 === 0,
                      [css.oddRow]: index % 2 !== 0,
                    })}
                  >
                    <td>
                      <Text
                        className={css.columnText}
                        type="white"
                        alignCenter
                        size="body"
                      >
                        {packageItem.name}
                      </Text>
                    </td>
                    <td>
                      <Text
                        className={css.columnText}
                        type="white"
                        alignCenter
                        size="body"
                      >
                        {packageItem.price}
                      </Text>
                    </td>
                    <td>
                      <Text
                        className={css.columnText}
                        type="white"
                        alignCenter
                        size="body"
                      >
                        {packageItem.boards}
                      </Text>
                    </td>
                    <td>
                      <Text
                        className={css.columnText}
                        type="white"
                        alignCenter
                        size="body"
                      >
                        {packageItem.players}
                      </Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {integrationPageContent.extraPackage && (
              <>
                <Text bold type="white" size="bodyLead" alignCenter>
                  {t("integrationPage.titles.extraPackage")}
                </Text>
                <Text type="white" size="body" alignCenter>
                  {t("integrationPage.body.extraPackage")}
                </Text>
              </>
            )}
          </div>
          <div className={css.textDescriptionContainer}>
            <Text
              bold
              alignCenter
              type="white"
              size="sectionTitle"
              className={css.sectionTitle}
              secondaryFamily
            >
              {t("integrationPage.titles.features")}
            </Text>
            {integrationPageContent.features.map((feature, index) => (
              <div
                key={`${index}-${feature.title}`}
                className={cn(css.features, {
                  [css.featuresReversed]: index % 2 !== 0,
                })}
              >
                <img
                  src={feature.img}
                  className={css.icons}
                  alt={feature.title}
                />
                <div className={css.featureDescriptionContainer}>
                  <Text className={css.textBody} type="white" size="body">
                    {feature.content}
                  </Text>
                </div>
              </div>
            ))}
          </div>
          <div className={css.imageContainer}>
            {splittedIntegrationImages &&
              splittedIntegrationImages.map((imageSet, index) => (
                <div key={index} className={css.imageRow}>
                  {imageSet.map((image) => (
                    <div key={image.title} className={css.imageWrapper}>
                      <img
                        src={image.src}
                        alt={image.alt}
                        title={image.title}
                        className={css.image}
                      />
                    </div>
                  ))}
                </div>
              ))}
          </div>
          <Button
            text={t("events.bookingButton")}
            className={css.bookingButton}
            onClick={() => {
              navigate(`/${city}/booking`);
            }}
          ></Button>
          <img
            src={gradientBar}
            alt="gradient Bar"
            className={css.gradientBar}
          />
          <div className={css.textDescriptionContainer}>
            <Text
              bold
              alignCenter
              type="white"
              size="sectionTitle"
              className={css.sectionTitle}
              secondaryFamily
            >
              {t("integrationPage.titles.additionals")}
            </Text>
            <AdditionalsView additionals={integrationPageContent.additionals} />
          </div>
          <div className={css.textDescriptionContainer}>
            <Text size="caption" type="white" bold>
              {t("integrationPage.additionals.starterDescription")}
            </Text>
            <Text alignCenter type="white" size="body" className={css.textBody}>
              {t("integrationPage.body.additionals")}
            </Text>
          </div>
          <div className={css.textDescriptionContainer}>
            <Text
              bold
              alignCenter
              type="white"
              size="sectionTitle"
              className={css.sectionTitle}
              secondaryFamily
            >
              {t("integrationPage.titles.offer")}
            </Text>
            <div className={css.offerContainer}>
              {currentCityConfig.cooperationSection.birthdayOfferVisibility && (
                <OfferItem
                  href={
                    currentCityConfig.cooperationSection.isHrefEnabled
                      ? `/${city}/birthday`
                      : null
                  }
                  image={school}
                  title={t("cooperationSection.birthdays")}
                />
              )}
              {currentCityConfig.cooperationSection.schoolOfferVisibility && (
                <OfferItem
                  href={
                    currentCityConfig.cooperationSection.isHrefEnabled
                      ? `/${city}/school`
                      : null
                  }
                  image={birthday}
                  title={t("cooperationSection.schools")}
                />
              )}
            </div>
          </div>
          <div className={css.faqContent}>
            <Text
              bold
              alignCenter
              type="white"
              size="sectionTitle"
              className={css.sectionTitle}
              secondaryFamily
            >
              {t("header.faq")}
            </Text>
            <FAQList content={content} setContent={setContent} />
          </div>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_EVENTS_RECAPTCHA_KEY}
            container={{
              element: "recaptchaContainer",
              parameters: {
                badge: "bottomleft",
                size: "invisible",
              },
            }}
          >
            <img
              src={gradientBar}
              alt="gradient Bar"
              className={css.gradientBar}
            />
            <IntegrationForm
              setIsSubmitLoading={setIsSubmitLoading}
              city={city}
            />
          </GoogleReCaptchaProvider>
        </div>
      </div>
      <img src={cubes} alt="Colorful cubes" className={css.cubesImage} />
      <Footer scrollCallback={footerScrollCallback} />
    </div>
  );
};
